import { SVGIconProps } from "@faire/slate/icons/types";
import { viewport } from "@faire/slate/lib/mediaQuery";
import { componentCls } from "@faire/slate/lib/tracking";
import {
  TOAST_TIMEOUT_MEDIUM_MS,
  TOAST_TIMEOUT_LARGE_MS,
} from "@faire/slate/private/toast/consts";
import { ToastBase } from "@faire/slate/private/toast/ToastBase";
import { delay } from "@faire/typescript/delay";
import React from "react";
import { Id, toast, ToastContentProps } from "react-toastify";

import {
  TOAST_CONTAINER_DESKTOP_ID,
  TOAST_CONTAINER_MOBILE_ID,
  TOAST_MOBILE_ID,
} from "./const";

type IProps = {
  /**
   * The CTA text of the toast. When provided, the close button will be replaced with the CTA button.
   *
   * When provided, the toast will be dismissed automatically after 6 seconds,
   * otherwise it will be dismissed after 4 seconds.
   */
  cta?: string;

  /**
   * The content of the toast.
   */
  description: string;

  /**
   * The icon of the toast.
   */
  Icon?: React.FC<SVGIconProps>;

  /**
   * **web**
   * The callback invoked when the CTA is actioned.
   */
  onCtaClick?: () => void;

  /**
   * **web**
   * The callback invoked when the toast is closed or the CTA is actioned.
   */
  onDismiss?: () => void;

  // DO NOT ADD any new prop without aligning with Slate team first
};

/**
 * Toasts contain information directly related to an action performed.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @version 2.0.0
 */
export const Toast = {
  /**
   * Shows a toast.
   * @trackfunction
   *
   * @param props - {@link IProps} of the toast to be shown.
   * @returns The toast Id.
   */
  create: createToast,
  /**
   * Closes a toast.
   * @trackfunction
   *
   * @param id - The Id of the toast to be closed.
   */
  dismiss: (id: Id) => toast.dismiss(id),
};

function createToast({
  onDismiss: onClose,
  description,
  Icon,
  cta,
  onCtaClick,
}: IProps): Id {
  const isMobile = viewport.sm()?.matches;

  const sendToast = (toastId?: string) => {
    // eslint-disable-next-line @faire/no-inline-hoc-fc
    return toast((props) => <ToastContent {...props} />, {
      onClose,
      data: { description, Icon, cta, onCtaClick },
      className: componentCls("fs-toast"),
      containerId: isMobile
        ? TOAST_CONTAINER_MOBILE_ID
        : TOAST_CONTAINER_DESKTOP_ID,
      toastId,
      autoClose: cta ? TOAST_TIMEOUT_LARGE_MS : TOAST_TIMEOUT_MEDIUM_MS,
    });
  };

  if (isMobile) {
    // We allow only one toast to be shown at a time on mobile
    if (toast.isActive(TOAST_MOBILE_ID)) {
      // We need to wait for the toast to be removed before sending a new one
      // otherwise the animation gets weird because the previous toast is still visible
      const listener = toast.onChange((event) => {
        if (event.status === "removed" && event.id === TOAST_MOBILE_ID) {
          listener();
          void delay(0).then(() => sendToast(TOAST_MOBILE_ID));
        }
      });
      toast.dismiss(TOAST_MOBILE_ID);

      return TOAST_MOBILE_ID;
    }

    return sendToast(TOAST_MOBILE_ID);
  }

  return sendToast();
}

const ToastContent = ({
  closeToast,
  data: { description, Icon, cta, onCtaClick },
}: ToastContentProps<IProps>) => {
  return (
    <ToastBase
      description={description}
      Icon={Icon}
      onDismiss={closeToast}
      cta={cta}
      onCtaClick={onCtaClick}
    />
  );
};
